import { Building2 } from 'lucide-react';
import React from 'react';
import { SOVEREIGN } from '../../utils/epcProjectVisual';
import { MyOrgUnit } from '../hooks/useActiveOrgUnit';

interface Props {
  items: MyOrgUnit[];
  activeOrgUnitId: string | null;
  onChange: (orgUnitId: string) => void;
}

/**
 * Single department switcher for multi-department managers / dept-heads.
 * Rendered ONCE per workspace — in EpcProjectDetailLayout, directly under the
 * hero tab bar — so every tab (DPR, Board, …) shares the same control. Tabs
 * must NOT render their own copy.
 */
export const DeptStrip: React.FC<Props> = ({ items, activeOrgUnitId, onChange }) => {
  if (items.length < 2) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-3 px-5 py-2.5"
      style={{
        background: SOVEREIGN.canvas,
        borderBottom: `1px solid ${SOVEREIGN.cardBorder}`,
      }}
    >
      <span
        className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em]"
        style={{ color: SOVEREIGN.kpiCaption }}
      >
        <Building2 size={13} strokeWidth={2.2} aria-hidden />
        Department
      </span>

      <div
        className="inline-flex items-center gap-0.5 rounded-xl p-0.5"
        style={{ background: SOVEREIGN.inputBg, border: `1px solid ${SOVEREIGN.inputBorder}` }}
        role="tablist"
        aria-label="Departments"
      >
        {items.map((it) => {
          const isActive = String(it.orgUnitId) === String(activeOrgUnitId);
          return (
            <button
              key={it.orgUnitId}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(it.orgUnitId)}
              className="rounded-[10px] px-3.5 py-1.5 text-[12.5px] font-semibold transition-all duration-150"
              style={
                isActive
                  ? {
                      background: `linear-gradient(180deg,#0E4636,${SOVEREIGN.title})`,
                      color: '#F8F2E2',
                      boxShadow: '0 6px 14px -8px rgba(11,59,46,0.55), inset 0 1px 0 rgba(255,255,255,0.10)',
                    }
                  : {
                      background: 'transparent',
                      color: SOVEREIGN.kpiCaption,
                    }
              }
              onMouseEnter={
                !isActive
                  ? (e) => {
                      (e.currentTarget as HTMLButtonElement).style.background = 'rgba(11,59,46,0.06)';
                      (e.currentTarget as HTMLButtonElement).style.color = SOVEREIGN.bottle;
                    }
                  : undefined
              }
              onMouseLeave={
                !isActive
                  ? (e) => {
                      (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                      (e.currentTarget as HTMLButtonElement).style.color = SOVEREIGN.kpiCaption;
                    }
                  : undefined
              }
            >
              {it.orgUnitName}
            </button>
          );
        })}
      </div>

      <span className="text-[11px] font-medium" style={{ color: SOVEREIGN.kpiCaption }}>
        {items.length} departments
      </span>
    </div>
  );
};

export default DeptStrip;
